'use client';
import React, { useState, useRef, useEffect } from 'react';
import { createClient } from '@/lib/supabase/client';
import { toast } from 'sonner';
import Icon from '@/components/ui/AppIcon';

interface MFAVerifyModalProps {
  factorId: string;
  onSuccess: () => void;
  onCancel: () => void;
}

export default function MFAVerifyModal({ factorId, onSuccess, onCancel }: MFAVerifyModalProps) {
  const [code, setCode] = useState(['', '', '', '', '', '']);
  const [isVerifying, setIsVerifying] = useState(false);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const supabase = createClient();

  useEffect(() => {
    inputRefs.current[0]?.focus();
  }, []);

  const handleChange = (index: number, value: string) => {
    if (!/^\d*$/.test(value)) return;
    const newCode = [...code];
    newCode[index] = value.slice(-1);
    setCode(newCode);
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (pasted.length === 6) {
      setCode(pasted.split(''));
      inputRefs.current[5]?.focus();
    }
  };

  const handleVerify = async () => {
    const totpCode = code.join('');
    if (totpCode.length !== 6) {
      toast.error('Wprowadź 6-cyfrowy kod');
      return;
    }
    setIsVerifying(true);
    try {
      const { data: challengeData, error: challengeError } = await supabase.auth.mfa.challenge({ factorId });
      if (challengeError) throw challengeError;

      const { error: verifyError } = await supabase.auth.mfa.verify({
        factorId,
        challengeId: challengeData.id,
        code: totpCode,
      });
      if (verifyError) throw verifyError;

      toast.success('Weryfikacja 2FA zakończona pomyślnie');
      onSuccess();
    } catch (err: any) {
      toast.error(err.message?.includes('Invalid') ? 'Nieprawidłowy kod — spróbuj ponownie' : 'Błąd weryfikacji 2FA');
      setCode(['', '', '', '', '', '']);
      inputRefs.current[0]?.focus();
    } finally {
      setIsVerifying(false);
    }
  };

  const fullCode = code.join('');

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm mx-4 p-8">
        {/* Header */}
        <div className="flex flex-col items-center mb-6">
          <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center mb-4">
            <Icon name="ShieldCheckIcon" size={28} className="text-primary" />
          </div>
          <h2 className="text-[20px] font-bold text-foreground text-center">Weryfikacja dwuetapowa</h2>
          <p className="text-[13px] text-muted-foreground text-center mt-1.5">
            Wprowadź 6-cyfrowy kod z aplikacji uwierzytelniającej (Google Authenticator, Authy itp.)
          </p>
        </div>

        {/* Code inputs */}
        <div className="flex gap-2 justify-center mb-6" onPaste={handlePaste}>
          {code.map((digit, i) => (
            <input
              key={`digit-${i}`}
              ref={el => { inputRefs.current[i] = el; }}
              type="text"
              inputMode="numeric"
              maxLength={1}
              value={digit}
              onChange={e => handleChange(i, e.target.value)}
              onKeyDown={e => handleKeyDown(i, e)}
              className={`w-11 h-13 text-center text-[20px] font-bold rounded-lg border-2 transition-all focus:outline-none focus:ring-0 ${
                digit ? 'border-primary bg-primary/5 text-primary' : 'border-input bg-white text-foreground'
              } focus:border-primary`}
              style={{ height: '52px' }}
            />
          ))}
        </div>

        {/* Security note */}
        <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-lg p-3 mb-5">
          <Icon name="LockIcon" size={14} className="text-amber-600 mt-0.5 shrink-0" />
          <p className="text-[11px] text-amber-700">
            Ochrona danych PHI — dostęp do danych pacjentów wymaga weryfikacji tożsamości zgodnie z wymogami KSO.
          </p>
        </div>

        {/* Actions */}
        <button
          onClick={handleVerify}
          disabled={isVerifying || fullCode.length !== 6}
          className="w-full py-2.5 rounded-lg bg-primary text-white text-[14px] font-semibold hover:bg-primary/90 transition-all disabled:opacity-50 flex items-center justify-center gap-2 mb-3"
        >
          {isVerifying ? (
            <>
              <Icon name="LoaderIcon" size={16} className="animate-spin" />
              <span>Weryfikacja...</span>
            </>
          ) : (
            <>
              <Icon name="ShieldCheckIcon" size={16} />
              <span>Zweryfikuj i zaloguj</span>
            </>
          )}
        </button>
        <button
          onClick={onCancel}
          className="w-full py-2 text-[13px] text-muted-foreground hover:text-foreground transition-colors"
        >
          Anuluj i wróć do logowania
        </button>
      </div>
    </div>
  );
}
